import React, { useState, useEffect } from 'react';
import { useTranslation } from '../hooks/useTranslation';
import { ingredientsService } from '../services/ingredientsService';
import { Ingredient } from 'public-contracts';
import { useListFetch } from '../hooks/useListFetch';
import { ListFetchStates } from '../components/common/ListFetchStates';

// shadcn/ui components
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';

// Lucide React icons
import { Plus, Edit, Trash2, Search, Beaker, ChefHat, ChevronUp, ChevronDown } from 'lucide-react';

interface IngredientListProps {
  onCreateClick: () => void;
  onEditClick: (ingredient: Ingredient) => void;
  refreshKey?: number; // Add refreshKey prop to trigger refresh
}

export const ModernIngredientList: React.FC<IngredientListProps> = ({
  onCreateClick,
  onEditClick,
  refreshKey,
}) => {
  const { translate } = useTranslation();
  const { data, isLoading, isError, fetchData } = useListFetch<{
    ingredients: Ingredient[];
    total: number;
  }>();
  const [ingredients, setIngredients] = useState<Ingredient[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [ingredientToDelete, setIngredientToDelete] =
    useState<Ingredient | null>(null);
  const [pagination, setPagination] = useState({
    page: 1,
    perPage: 20,
    total: 0,
  });
  const [sort, setSort] = useState<{ field: string; order: 'ASC' | 'DESC' }>({
    field: 'name',
    order: 'ASC',
  });

  // Debounce search term and reset to page 1 when search changes
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  useEffect(() => {
    if (searchTerm && pagination.page !== 1) {
      setPagination((prev) => ({ ...prev, page: 1 }));
    }
  }, [searchTerm]);

  // Fetch data - follows OrdersReport pattern exactly
  const handleFetchIngredients = async () => {
    try {
      const response = await fetchData(async () => {
        const result = await ingredientsService.getIngredients({
          page: pagination.page,
          perPage: pagination.perPage,
          q: debouncedSearchTerm || undefined,
          sort: { [sort.field]: sort.order === 'ASC' ? 1 : -1 },
        });

        const ingredientsArray = Array.isArray(result) ? result : [];
        const total = (result as any).__total || ingredientsArray.length;

        return {
          ingredients: ingredientsArray,
          total,
        };
      });

      if (response) {
        setIngredients(response.ingredients);
        setPagination((prev) => ({ ...prev, total: response.total }));
      }
    } catch (error) {
      // Error is already handled by useListFetch
      setIngredients([]);
      setPagination((prev) => ({ ...prev, total: 0 }));
    }
  };

  // Auto-fetch data when component mounts or dependencies change
  useEffect(() => {
    // Debounce search to avoid too many API calls
    const timer = setTimeout(
      () => {
        handleFetchIngredients();
      },
      debouncedSearchTerm ? 300 : 0
    );

    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.page, pagination.perPage, debouncedSearchTerm, sort.field, sort.order, refreshKey]);

  const handlePageChange = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  // Handle sorting
  const handleSort = (field: string) => {
    const newOrder =
      sort.field === field && sort.order === 'ASC' ? 'DESC' : 'ASC';
    setSort({ field, order: newOrder });
    // Reset to page 1 when sorting changes
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  // Get sort icon for a field
  const getSortIcon = (field: string) => {
    if (sort.field !== field) return null;
    return sort.order === 'ASC' ? (
      <ChevronUp className="h-4 w-4" />
    ) : (
      <ChevronDown className="h-4 w-4" />
    );
  };

  const handleDelete = async () => {
    if (!ingredientToDelete) return;

    try {
      await ingredientsService.deleteIngredient(ingredientToDelete.id);
      setIngredients((prev) =>
        prev.filter((i) => i.id !== ingredientToDelete.id)
      );
      setDeleteDialogOpen(false);
      setIngredientToDelete(null);
      // Refresh the list after deletion
      handleFetchIngredients();
    } catch (err) {
      console.error('Failed to delete ingredient:', err);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center space-x-3">
          <Beaker className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">
            {translate('pos.ingredients.name', 'Ingredient Management')}
          </h1>
        </div>
        <Button onClick={onCreateClick} className="flex items-center space-x-2">
          <Plus className="h-4 w-4" />
          <span>{translate('pos.ingredients.add', 'Add Ingredient')}</span>
        </Button>
      </div>

      {/* Search */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Search className="h-5 w-5" />
            <span>{translate('common.search', 'Search')}</span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex space-x-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder={translate(
                  'pos.ingredients.search',
                  'Search ingredients...'
                )}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Loading and Error States - matches OrdersReport pattern */}
      <ListFetchStates
        isLoading={isLoading}
        isError={isError}
        onRetry={handleFetchIngredients}
        loadingMessage={translate('common.loading', 'Loading ingredients...')}
        errorMessage={translate(
          'pos.ingredients.load_error',
          'Failed to load ingredients'
        )}
      />

      {/* Ingredient Table - only show when not loading and no error */}
      {!isLoading && !isError && (
        <Card>
          <CardHeader>
            <CardTitle>
              {translate('pos.ingredients.list', 'Ingredients')}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {ingredients.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                {translate('pos.ingredients.empty', 'No ingredients found')}
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>
                      <button
                        onClick={() => handleSort('name')}
                        className="flex items-center gap-1 hover:text-foreground"
                      >
                        {translate('pos.ingredients.fields.name', 'Name')}
                        {getSortIcon('name')}
                      </button>
                    </TableHead>
                    <TableHead>
                      {translate(
                        'pos.ingredients.fields.description',
                        'Description'
                      )}
                    </TableHead>
                    <TableHead>
                      {translate('pos.ingredients.fields.recipe', 'Recipe')}
                    </TableHead>
                    <TableHead>
                      {translate('common.actions', 'Actions')}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {ingredients.map((ingredient) => (
                    <TableRow key={ingredient.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <span>{ingredient.name}</span>
                          {ingredient.recipeId && (
                            <ChefHat
                              className="h-4 w-4 text-primary"
                              title={translate(
                                'pos.ingredients.recipe.hasRecipe',
                                'This ingredient has a recipe'
                              )}
                            />
                          )}
                        </div>
                      </TableCell>
                      <TableCell>{ingredient.description || '-'}</TableCell>
                      <TableCell>
                        {ingredient.recipeId ? (
                          <div className="flex items-center space-x-1 text-primary">
                            <ChefHat className="h-4 w-4" />
                            <span className="text-sm">
                              {translate(
                                'pos.ingredients.recipe.hasRecipe',
                                'Has recipe'
                              )}
                            </span>
                          </div>
                        ) : (
                          <span className="text-sm text-muted-foreground">
                            {translate('pos.ingredients.recipe.none', 'No recipe')}
                          </span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-2">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => onEditClick(ingredient)}
                          >
                            <Edit className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              setIngredientToDelete(ingredient);
                              setDeleteDialogOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {translate('pos.ingredients.delete', 'Delete Ingredient')}
            </DialogTitle>
            <DialogDescription>
              {translate('pos.ingredients.delete.confirm', {
                name: ingredientToDelete?.name || '',
              })}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setDeleteDialogOpen(false)}
            >
              {translate('common.cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              {translate('common.delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pagination */}
      {pagination.total > 0 && (
        <div className="sticky bottom-0 left-0 right-0 bg-background border-t border-border shadow-lg z-20">
          <div className="flex flex-col sm:flex-row items-center justify-between p-4 gap-4">
            <div className="text-sm text-muted-foreground text-center sm:text-left">
              {translate('pos.orders.modern.pagination.showing', {
                start: Math.min(
                  (pagination.page - 1) * pagination.perPage + 1,
                  pagination.total
                ),
                end: Math.min(
                  pagination.page * pagination.perPage,
                  pagination.total
                ),
                total: pagination.total,
              })}{' '}
              {translate('pos.ingredients.list', 'ingredients')}
            </div>
            <div className="flex items-center space-x-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.previous',
                  'Previous'
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={
                  pagination.page * pagination.perPage >= pagination.total
                }
                className="border-border text-foreground hover:bg-muted disabled:opacity-50"
              >
                {translate(
                  'pos.orders.modern.pagination.next',
                  'Next'
                )}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
